/**
 * Units API - Cross-hangar unit management
 *
 * Extends the base hangar-units API with queries that join
 * building and location data for the standalone Units page.
 */

import { supabase } from '@/lib/supabase'
import type {
  UnitTableRow,
  UnitFilters,
  UnitFormData,
  AreaOption,
  UnitStatus,
} from '@/types/units'

// Re-export base functions for convenience
export {
  fetchHangarUnit,
  createHangarUnit,
  createHangarUnits,
  updateHangarUnit,
  deleteHangarUnit,
  deleteAllHangarUnits,
} from './hangar-units'

/**
 * Fetch all units across all buildings with building/location info.
 * Used for the standalone Units page.
 */
export async function fetchAllUnits(filters?: UnitFilters): Promise<UnitTableRow[]> {
  let query = supabase
    .from('units')
    .select(`
      id,
      unit_code,
      name,
      sequence,
      area_id,
      organization_id,
      type,
      capacity_strategy,
      interior_width,
      interior_depth,
      interior_height,
      usable_sqft,
      door_width,
      depth,
      tail_width,
      door_height,
      door_type,
      max_wingspan,
      max_length,
      max_tail_height,
      offset_x,
      offset_y,
      unit_rotation,
      status,
      is_leasable,
      current_lease_id,
      climate_controlled,
      heated,
      electrical_service,
      features,
      default_monthly_rate,
      default_transient_rate,
      rate_type,
      pricing_override,
      unit_monthly_rate,
      unit_rate_per_sqft,
      created_at,
      updated_at,
      areas:area_id (
        name,
        area_code,
        type,
        location_id,
        locations:location_id (
          name,
          location_code
        )
      )
    `)
    .is('deleted_at', null)

  // Apply filters
  if (filters?.type && filters.type.length > 0) {
    query = query.in('type', filters.type)
  }

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.capacityStrategy && filters.capacityStrategy.length > 0) {
    query = query.in('capacity_strategy', filters.capacityStrategy)
  }

  if (filters?.areaId) {
    query = query.eq('area_id', filters.areaId)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.isLeasable !== undefined) {
    query = query.eq('is_leasable', filters.isLeasable)
  }

  // Order by building name, then sequence
  query = query.order('sequence')

  const { data, error } = await query

  if (error) throw error

  // Transform the nested data to flat structure
  return (data ?? []).map((row): UnitTableRow => {
    const area = row.areas as {
      name: string
      area_code: string
      type: string
      location_id: string | null
      locations: { name: string; location_code: string } | null
    } | null

    return {
      id: row.id,
      unit_code: row.unit_code,
      name: row.name,
      sequence: row.sequence,
      area_id: row.area_id,
      organization_id: row.organization_id,
      location_id: area?.location_id ?? '',
      // Classification
      type: row.type,
      capacity_strategy: row.capacity_strategy,
      // Interior Dimensions
      interior_width: row.interior_width,
      interior_depth: row.interior_depth,
      interior_height: row.interior_height,
      usable_sqft: row.usable_sqft,
      // T-hangar dimensions
      door_width: row.door_width,
      depth: row.depth,
      tail_width: row.tail_width,
      // Door Specifications
      door_height: row.door_height,
      door_type: row.door_type,
      // Aircraft Constraints
      max_wingspan: row.max_wingspan,
      max_length: row.max_length,
      max_tail_height: row.max_tail_height,
      // Position
      offset_x: row.offset_x,
      offset_y: row.offset_y,
      unit_rotation: row.unit_rotation,
      // Status
      status: (row.status as UnitStatus) || 'available',
      is_leasable: row.is_leasable,
      current_lease_id: row.current_lease_id,
      // Features
      climate_controlled: row.climate_controlled,
      heated: row.heated,
      electrical_service: row.electrical_service,
      features: typeof row.features === 'string' ? JSON.parse(row.features as string) : row.features,
      // Pricing
      default_monthly_rate: row.default_monthly_rate,
      default_transient_rate: row.default_transient_rate,
      rate_type: row.rate_type,
      pricing_override: row.pricing_override,
      unit_monthly_rate: row.unit_monthly_rate,
      unit_rate_per_sqft: row.unit_rate_per_sqft,
      // Joined area info
      area_name: area?.name ?? 'Unknown',
      area_code: area?.area_code ?? '',
      area_type: area?.type ?? '',
      // Joined location info
      location_name: area?.locations?.name ?? null,
      location_code: area?.locations?.location_code ?? null,
      // Audit
      created_at: row.created_at,
      updated_at: row.updated_at,
    }
  })
}

/**
 * Fetch a single unit with building/location context
 */
export async function fetchUnitWithDetails(id: string): Promise<UnitTableRow> {
  const { data, error } = await supabase
    .from('units')
    .select(`
      id,
      unit_code,
      name,
      sequence,
      area_id,
      organization_id,
      type,
      capacity_strategy,
      interior_width,
      interior_depth,
      interior_height,
      usable_sqft,
      door_width,
      depth,
      tail_width,
      door_height,
      door_type,
      max_wingspan,
      max_length,
      max_tail_height,
      offset_x,
      offset_y,
      unit_rotation,
      status,
      is_leasable,
      current_lease_id,
      climate_controlled,
      heated,
      electrical_service,
      features,
      default_monthly_rate,
      default_transient_rate,
      rate_type,
      pricing_override,
      unit_monthly_rate,
      unit_rate_per_sqft,
      created_at,
      updated_at,
      areas:area_id (
        name,
        area_code,
        type,
        location_id,
        locations:location_id (
          name,
          location_code
        )
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const area = data.areas as {
    name: string
    area_code: string
    type: string
    location_id: string | null
    locations: { name: string; location_code: string } | null
  } | null

  return {
    id: data.id,
    unit_code: data.unit_code,
    name: data.name,
    sequence: data.sequence,
    area_id: data.area_id,
    organization_id: data.organization_id,
    location_id: area?.location_id ?? '',
    // Classification
    type: data.type,
    capacity_strategy: data.capacity_strategy,
    // Interior Dimensions
    interior_width: data.interior_width,
    interior_depth: data.interior_depth,
    interior_height: data.interior_height,
    usable_sqft: data.usable_sqft,
    // T-hangar dimensions
    door_width: data.door_width,
    depth: data.depth,
    tail_width: data.tail_width,
    // Door Specifications
    door_height: data.door_height,
    door_type: data.door_type,
    // Aircraft Constraints
    max_wingspan: data.max_wingspan,
    max_length: data.max_length,
    max_tail_height: data.max_tail_height,
    // Position
    offset_x: data.offset_x,
    offset_y: data.offset_y,
    unit_rotation: data.unit_rotation,
    // Status
    status: (data.status as UnitStatus) || 'available',
    is_leasable: data.is_leasable,
    current_lease_id: data.current_lease_id,
    // Features
    climate_controlled: data.climate_controlled,
    heated: data.heated,
    electrical_service: data.electrical_service,
    features: typeof data.features === 'string' ? JSON.parse(data.features as string) : data.features,
    // Pricing
    default_monthly_rate: data.default_monthly_rate,
    default_transient_rate: data.default_transient_rate,
    rate_type: data.rate_type,
    pricing_override: data.pricing_override,
    unit_monthly_rate: data.unit_monthly_rate,
    unit_rate_per_sqft: data.unit_rate_per_sqft,
    // Joined area info
    area_name: area?.name ?? 'Unknown',
    area_code: area?.area_code ?? '',
    area_type: area?.type ?? '',
    // Joined location info
    location_name: area?.locations?.name ?? null,
    location_code: area?.locations?.location_code ?? null,
    // Audit
    created_at: data.created_at,
    updated_at: data.updated_at,
  }
}

/**
 * Fetch areas available for unit creation (T-hangar types only)
 */
export async function fetchAreasForUnits(): Promise<AreaOption[]> {
  const { data, error } = await supabase
    .from('areas')
    .select(`
      id,
      name,
      area_code,
      type,
      locations:location_id (
        name
      )
    `)
    .in('type', ['t_hangar_row', 'nested_t_hangar_row'])
    .is('deleted_at', null)
    .order('name')

  if (error) throw error

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    area_code: row.area_code,
    type: row.type,
    location_name: (row.locations as { name: string } | null)?.name ?? null,
  }))
}

/**
 * Get the next sequence number for an area
 */
export async function getNextUnitSequence(areaId: string): Promise<number> {
  const { data, error } = await supabase
    .from('units')
    .select('sequence')
    .eq('area_id', areaId)
    .is('deleted_at', null)
    .order('sequence', { ascending: false })
    .limit(1)

  if (error) throw error

  if (!data || data.length === 0) {
    return 1
  }

  return data[0].sequence + 1
}

/**
 * Create a new unit with auto-generated sequence
 */
export async function createUnit(
  data: Omit<UnitFormData, 'sequence'> & { sequence?: number }
): Promise<UnitTableRow> {
  // Get next sequence if not provided
  const sequence = data.sequence ?? await getNextUnitSequence(data.area_id)

  // Get organization_id and location_id from the area
  const { data: area, error: areaError } = await supabase
    .from('areas')
    .select('organization_id, location_id')
    .eq('id', data.area_id)
    .single()

  if (areaError) throw areaError

  const { data: created, error } = await supabase
    .from('units')
    .insert({
      area_id: data.area_id,
      organization_id: area.organization_id,
      location_id: area.location_id,
      unit_code: data.unit_code.toUpperCase(),
      name: data.name ?? null,
      sequence,
      // Classification
      type: data.type ?? null,
      capacity_strategy: data.capacity_strategy ?? null,
      // Interior Dimensions
      interior_width: data.interior_width ?? null,
      interior_depth: data.interior_depth ?? null,
      interior_height: data.interior_height ?? null,
      usable_sqft: data.usable_sqft ?? null,
      // T-hangar dimensions
      door_width: data.door_width ?? null,
      depth: data.depth ?? null,
      tail_width: data.tail_width ?? null,
      // Door Specifications
      door_height: data.door_height ?? null,
      door_type: data.door_type ?? null,
      // Aircraft Constraints
      max_wingspan: data.max_wingspan ?? null,
      max_length: data.max_length ?? null,
      max_tail_height: data.max_tail_height ?? null,
      // Position
      offset_x: data.offset_x ?? 0,
      offset_y: data.offset_y ?? 0,
      unit_rotation: data.unit_rotation ?? null,
      // Status
      status: data.status ?? 'available',
      is_leasable: data.is_leasable ?? true,
      // Features
      climate_controlled: data.climate_controlled ?? false,
      heated: data.heated ?? false,
      electrical_service: data.electrical_service ?? null,
      // Pricing
      default_monthly_rate: data.default_monthly_rate ?? null,
      default_transient_rate: data.default_transient_rate ?? null,
      rate_type: data.rate_type ?? null,
      pricing_override: data.pricing_override ?? false,
      unit_monthly_rate: data.unit_monthly_rate ?? null,
      unit_rate_per_sqft: data.unit_rate_per_sqft ?? null,
    })
    .select()
    .single()

  if (error) throw error

  // Fetch with full details for return
  return fetchUnitWithDetails(created.id)
}

/**
 * Update a unit
 */
export async function updateUnit(
  id: string,
  data: Partial<UnitFormData>
): Promise<UnitTableRow> {
  const updateData: Record<string, unknown> = {}

  // Basic Info
  if (data.unit_code !== undefined) updateData.unit_code = data.unit_code.toUpperCase()
  if (data.name !== undefined) updateData.name = data.name
  if (data.sequence !== undefined) updateData.sequence = data.sequence
  // Classification
  if (data.type !== undefined) updateData.type = data.type
  if (data.capacity_strategy !== undefined) updateData.capacity_strategy = data.capacity_strategy
  // Interior Dimensions
  if (data.interior_width !== undefined) updateData.interior_width = data.interior_width
  if (data.interior_depth !== undefined) updateData.interior_depth = data.interior_depth
  if (data.interior_height !== undefined) updateData.interior_height = data.interior_height
  if (data.usable_sqft !== undefined) updateData.usable_sqft = data.usable_sqft
  // T-hangar dimensions
  if (data.door_width !== undefined) updateData.door_width = data.door_width
  if (data.depth !== undefined) updateData.depth = data.depth
  if (data.tail_width !== undefined) updateData.tail_width = data.tail_width
  // Door Specifications
  if (data.door_height !== undefined) updateData.door_height = data.door_height
  if (data.door_type !== undefined) updateData.door_type = data.door_type
  // Aircraft Constraints
  if (data.max_wingspan !== undefined) updateData.max_wingspan = data.max_wingspan
  if (data.max_length !== undefined) updateData.max_length = data.max_length
  if (data.max_tail_height !== undefined) updateData.max_tail_height = data.max_tail_height
  // Position
  if (data.offset_x !== undefined) updateData.offset_x = data.offset_x
  if (data.offset_y !== undefined) updateData.offset_y = data.offset_y
  if (data.unit_rotation !== undefined) updateData.unit_rotation = data.unit_rotation
  // Status
  if (data.is_leasable !== undefined) updateData.is_leasable = data.is_leasable
  if (data.status !== undefined) updateData.status = data.status
  // Features
  if (data.climate_controlled !== undefined) updateData.climate_controlled = data.climate_controlled
  if (data.heated !== undefined) updateData.heated = data.heated
  if (data.electrical_service !== undefined) updateData.electrical_service = data.electrical_service
  // Pricing
  if (data.default_monthly_rate !== undefined) updateData.default_monthly_rate = data.default_monthly_rate
  if (data.default_transient_rate !== undefined) updateData.default_transient_rate = data.default_transient_rate
  if (data.rate_type !== undefined) updateData.rate_type = data.rate_type
  if (data.pricing_override !== undefined) updateData.pricing_override = data.pricing_override
  if (data.unit_monthly_rate !== undefined) updateData.unit_monthly_rate = data.unit_monthly_rate
  if (data.unit_rate_per_sqft !== undefined) updateData.unit_rate_per_sqft = data.unit_rate_per_sqft

  const { error } = await supabase
    .from('units')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Fetch with full details for return
  return fetchUnitWithDetails(id)
}

/**
 * Soft delete a unit
 */
export async function deleteUnit(id: string): Promise<void> {
  const { error } = await supabase
    .from('units')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Get unit count by area (for summary displays)
 */
export async function getUnitCountByArea(
  areaId: string
): Promise<{ total: number; available: number; leased: number; maintenance: number }> {
  const { data, error } = await supabase
    .from('units')
    .select('status')
    .eq('area_id', areaId)
    .is('deleted_at', null)

  if (error) throw error

  const units = data ?? []
  return {
    total: units.length,
    available: units.filter((u) => u.status === 'available').length,
    leased: units.filter((u) => u.status === 'leased').length,
    maintenance: units.filter((u) => u.status === 'maintenance').length,
  }
}
